import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, TrendingUp, KeyRound, Percent, CalendarDays, PhoneCall, Truck, AlertTriangle } from "lucide-react";
import { fmtEUR, fmtDate } from "@/lib/format";
import { KpiCard, Widget, Priorites, Ligne, DetailModal, chargerDetailKpi, libPeriode, TendanceSub } from "@/components/dashboards/commun";

const TYPE_COULEURS = { essai: "#16A34A", showroom: "#2F6FED", livraison: "#E8853D", relance: "#7C5CE0", reunion: "#14213D", perso: "#64748B", externe: "#94A3B8" };

export const DashVendeur = ({ d }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const k = d.kpis, w = d.widgets;
  const ouvrir = (cle) => chargerDetailKpi(cle, setDetail, { periode: d.periode });
  return (
    <div data-testid="dash-vendeur">
      {w.priorites && <Priorites items={w.priorites} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {k.ventes_mois && (
          <KpiCard icon={Car} couleur="#2F6FED" titre={libPeriode(d.periode, "Ventes")} testId="kpi-ventes-mois" onClick={() => ouvrir("ventes_mois")}
            valeur={`${k.ventes_mois.valeur} signée${k.ventes_mois.valeur > 1 ? "s" : ""} / ${k.ventes_mois.livraisons ?? 0} livrée${(k.ventes_mois.livraisons ?? 0) > 1 ? "s" : ""}`}
            sub={<TendanceSub kpi={k.ventes_mois} periode={d.periode} />}>
            <div className="h-1.5 bg-[#EEF1F6] rounded-full mt-1.5" title={`Commandes signées vs objectif : ${k.ventes_mois.valeur} / ${k.ventes_mois.objectif}`}>
              <div className="h-1.5 rounded-full bg-[#2F6FED]" style={{ width: `${Math.min(k.ventes_mois.valeur / k.ventes_mois.objectif * 100, 100)}%` }} />
            </div>
          </KpiCard>
        )}
        {k.marge_mois && <KpiCard icon={TrendingUp} couleur="#16A34A" titre={libPeriode(d.periode, "Marge")} valeur={fmtEUR(k.marge_mois.valeur)} testId="kpi-marge-mois" onClick={() => ouvrir("marge_mois")} sub={<TendanceSub kpi={k.marge_mois} periode={d.periode} />} />}
        {k.essais_realises && <KpiCard icon={KeyRound} couleur="#E8853D" titre={libPeriode(d.periode, "Essais réalisés")} valeur={k.essais_realises.valeur} testId="kpi-essais-realises" onClick={() => ouvrir("essais_realises")} sub={<TendanceSub kpi={k.essais_realises} periode={d.periode} />} />}
        {k.taux_conversion && <KpiCard icon={Percent} couleur="#7C5CE0" titre="Taux de conversion (90 j)" valeur={k.taux_conversion.valeur != null ? `${k.taux_conversion.valeur} %` : "—"} testId="kpi-taux-conversion" onClick={() => ouvrir("taux_conversion")} sub={<TendanceSub kpi={k.taux_conversion} periode={d.periode} />} />}
      </div>
      <DetailModal detail={detail} onClose={() => setDetail(null)} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {w.ma_journee && (
          <Widget titre="Ma journée" icon={CalendarDays} testId="widget-ma-journee" vide={w.ma_journee.length === 0 && "Aucun rendez-vous aujourd'hui."}>
            {w.ma_journee.map((e) => (
              <Ligne key={e.cle} onClick={() => navigate("/agenda")} testId={`journee-${e.cle}`}>
                <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: TYPE_COULEURS[e.type] || "#94A3B8" }} />
                <span className="font-mono text-[12px] text-[--idc-muted] shrink-0">{e.heure}</span>
                <span className="text-[13px] font-medium truncate">{e.titre}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.mes_dossiers_bloques && (
          <Widget titre="Mes dossiers bloqués" icon={AlertTriangle} testId="widget-dossiers-bloques"
            vide={w.mes_dossiers_bloques.length === 0 && "Aucun blocage administratif sur vos dossiers. 👍"}>
            {w.mes_dossiers_bloques.map((b) => (
              <Ligne key={b.dossier_id} onClick={() => navigate(`/ventes/${b.dossier_id}`)} testId={`bloque-${b.numero}`}>
                <span className="font-mono text-[12px] shrink-0">{b.numero}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{b.blocage}</div>
                  <div className="text-[11px] text-[--idc-muted]">resp. {b.responsable}{b.depuis ? ` · depuis ${fmtDate(b.depuis.slice(0, 10))}` : ""}{b.nb_blocages > 1 ? ` · +${b.nb_blocages - 1} autre(s)` : ""}</div>
                </div>
                <span className="pill shrink-0" style={{ background: b.criticite === "critique" ? "#FEE2E2" : "#FEF3C7", color: b.criticite === "critique" ? "#B91C1C" : "#B45309" }}>
                  {b.criticite}
                </span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.opportunites_relancer && (
          <Widget titre="Mes opportunités à relancer" icon={PhoneCall} testId="widget-opps-relancer" vide={w.opportunites_relancer.length === 0 && "Aucune relance en retard. 👍"}>
            {w.opportunites_relancer.map((o) => (
              <Ligne key={o.id} onClick={() => navigate(`/pipeline/opportunites/${o.id}`)} testId={`relancer-${o.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-medium truncate">{o.client} — {o.constat || "Opportunité"}</div>
                  <div className="text-[11.5px] text-[#B91C1C]">Relance prévue le {fmtDate(o.date_relance)} · {o.montant ? fmtEUR(o.montant) : ""}</div>
                </div>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.essais_venir && (
          <Widget titre="Mes essais à venir" icon={KeyRound} testId="widget-essais-venir" vide={w.essais_venir.length === 0 && "Aucun essai planifié."}>
            {w.essais_venir.map((e) => (
              <Ligne key={e.id} onClick={() => e.opportunite_id ? navigate(`/pipeline/opportunites/${e.opportunite_id}`) : navigate("/agenda")} testId={`essai-${e.id}`}>
                <span className="font-mono text-[12px] text-[--idc-muted] shrink-0">{fmtDate(e.date)} {e.heure}</span>
                <span className="text-[13px] font-medium truncate">{e.client}</span>
              </Ligne>
            ))}
          </Widget>
        )}
        {w.livraisons_semaine && (
          <Widget titre="Mes livraisons de la semaine" icon={Truck} testId="widget-livraisons" vide={w.livraisons_semaine.length === 0 && "Aucune livraison prévue cette semaine."}>
            {w.livraisons_semaine.map((l) => (
              <Ligne key={l.id} onClick={() => l.dossier_id ? navigate(`/ventes/${l.dossier_id}`) : navigate("/agenda")} testId={`livraison-${l.id}`}>
                <span className="font-mono text-[12px] text-[--idc-muted] shrink-0">{fmtDate(l.date)} {l.heure}</span>
                <span className="text-[13px] font-medium truncate">{l.titre}</span>
              </Ligne>
            ))}
          </Widget>
        )}
      </div>
    </div>
  );
};
